import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { Alert, Button, Checkbox, Modal, Popconfirm, Space, Table, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDateTime, formatNumber } from 'pages/star-shared/utils';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import {
  BatchRow,
  dayGroups,
  defaultSelection,
  gapDays,
  isOverwrite,
  rowDay,
  sortRows,
  toggleRow,
} from '../batch-utils';
import { useDataImport } from '../context';
import { ImportResult } from '../types';
import {
  daysListText,
  formatDay,
  formatLiveHours,
  importErrorMessage,
  isNotAllowed,
  isStudioBlocked,
  normalizeResult,
  weekdayOf,
} from '../utils';
import ImportResultPanel from './import-result-panel';
import styles from '../style.module.scss';

interface Props {
  files: File[];
  /** Đã ghi xong ít nhất một ngày — nơi gọi tải lại dải lịch. `month`: tháng của ngày cuối. */
  onApplied: (month: string | null) => void;
  /** Đóng khối hàng loạt, quay lại ô chọn file. */
  onClose: () => void;
}

/** Bị giới hạn tần suất (429): đợi rồi gửi lại, tối đa vài lần. */
const THROTTLE_WAIT_MS = 20_000;
const THROTTLE_RETRIES = 4;

const isThrottled = (err: any) => {
  const status = err?.statusCode || err?.status;
  return status === 429 || /ThrottlerException|Too Many Requests/i.test(String(err?.message || ''));
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const STATUS_VIEW: Record<BatchRow['status'], { color: string; label: string }> = {
  queued: { color: 'default', label: 'Chờ đọc' },
  reading: { color: 'processing', label: 'Đang đọc' },
  ready: { color: 'blue', label: 'Sẵn sàng' },
  blocked: { color: 'red', label: 'Bị chặn' },
  error: { color: 'red', label: 'Lỗi file' },
  applying: { color: 'processing', label: 'Đang nhập' },
  done: { color: 'green', label: 'Đã nhập' },
  failed: { color: 'red', label: 'Nhập lỗi' },
};

/**
 * Nhập nhiều file "Dữ liệu nhà sáng tạo" một lượt. Mỗi file được xem trước lần lượt;
 * máy chủ đọc cột "Giai đoạn dữ liệu" để biết file là ngày nào (người nhập không chọn ngày).
 * Bảng xếp theo ngày, đánh dấu file trùng ngày, ngày bị bỏ trống, ngày sẽ ghi đè; bấm
 * "Nhập" thì gửi lần lượt từng file như luồng một file (mỗi ngày một lần nhập + chấm Sao).
 */
const BatchImport = ({ files, onApplied, onClose }: Props) => {
  const { reportError } = useDataImport();
  const [rows, setRows] = useState<BatchRow[]>(() =>
    files.map((file, index) => ({
      key: `${index}-${file.name}-${file.size}`,
      file,
      status: 'queued',
      selected: false,
    }))
  );
  const [running, setRunning] = useState<'preview' | 'apply' | null>('preview');
  const [detailKey, setDetailKey] = useState<string | null>(null);
  const alive = useRef(true);
  // Dừng giữa chừng: file đang gửi vẫn chạy hết, các file sau không gửi nữa.
  const stopRef = useRef(false);

  // Vòng xem trước chỉ chạy một lần (StrictMode gắn effect hai lần).
  const started = useRef(false);

  useEffect(() => {
    alive.current = true;
    stopRef.current = false;
    return () => {
      alive.current = false;
      stopRef.current = true;
    };
  }, []);

  const patch = useCallback((key: string, change: Partial<BatchRow>) => {
    if (!alive.current) return;
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...change } : row)));
  }, []);

  /** Gửi một file; 429 thì đợi rồi gửi lại. Lỗi khác ném ra cho nơi gọi. */
  const send = useCallback(async (step: 'preview' | 'apply', file: File) => {
    for (let attempt = 0; ; attempt += 1) {
      const form = new FormData();
      form.append('file', file);
      form.append('kind', 'DAILY');
      try {
        const res: any = await AxiosUtils.post(
          step === 'preview' ? API.dataImportPreview : API.dataImportApply,
          form,
          { 'Content-Type': 'multipart/form-data' }
        );
        return normalizeResult(res?.data);
      } catch (err) {
        if (!isThrottled(err) || attempt >= THROTTLE_RETRIES || stopRef.current) throw err;
        await sleep(THROTTLE_WAIT_MS);
      }
    }
  }, []);

  /** Lỗi mất quyền / cổng Studio: báo cả trang (trang tự chuyển sang khối chưa có quyền). */
  const fatal = (err: unknown) => isNotAllowed(err) || isStudioBlocked(err);

  // Xem trước lần lượt từng file (không ghi gì).
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      const queue = rows.filter((row) => row.status === 'queued');
      for (const row of queue) {
        if (stopRef.current) break;
        patch(row.key, { status: 'reading' });
        try {
          const result = await send('preview', row.file);
          patch(row.key, {
            status: result.blockers.length ? 'blocked' : 'ready',
            result,
          });
        } catch (err) {
          if (fatal(err)) {
            reportError(err, 'Không đọc được file');
            break;
          }
          patch(row.key, { status: 'error', error: importErrorMessage(err) });
        }
      }
      if (!alive.current) return;
      setRows((prev) => defaultSelection(prev));
      setRunning(null);
    })();
    // Chỉ chạy một lần cho bộ file vừa chọn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sorted = useMemo(() => sortRows(rows), [rows]);
  const groups = useMemo(() => dayGroups(rows), [rows]);
  const selected = sorted.filter((row) => row.selected);
  const days = rows
    .map(rowDay)
    .filter((day): day is string => !!day)
    .sort();
  const gaps = useMemo(() => gapDays(days), [days.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const conflictDays = Array.from(
    new Set(
      sorted
        .filter((row) => {
          const info = groups.get(row.key);
          return info && info.count > 1 && !info.identical;
        })
        .map((row) => rowDay(row) as string)
    )
  );
  const conflictsOpen = conflictDays.filter(
    (day) => !rows.some((row) => row.selected && rowDay(row) === day)
  );
  const overwriteCount = selected.filter(isOverwrite).length;
  const blockedCount = rows.filter(
    (row) => row.status === 'blocked' || row.status === 'error'
  ).length;
  const doneCount = rows.filter((row) => row.status === 'done').length;
  const readCount = rows.filter(
    (row) => row.status !== 'queued' && row.status !== 'reading'
  ).length;

  const applyAll = async () => {
    stopRef.current = false;
    setRunning('apply');
    let lastMonth: string | null = null;
    // Ngày cũ trước: chấm Sao / mốc KC đi theo thứ tự thời gian.
    for (const row of selected) {
      if (stopRef.current) break;
      patch(row.key, { status: 'applying', error: undefined });
      try {
        const result = await send('apply', row.file);
        patch(row.key, { status: 'done', result, selected: false });
        lastMonth = result.month || lastMonth;
      } catch (err) {
        if (fatal(err)) {
          reportError(err, `Nhập file ${row.file.name} thất bại`);
          patch(row.key, { status: 'failed', error: importErrorMessage(err) });
          break;
        }
        patch(row.key, { status: 'failed', error: importErrorMessage(err) });
      }
    }
    if (!alive.current) return;
    setRunning(null);
    onApplied(lastMonth);
  };

  const columns: ColumnsType<BatchRow> = [
    {
      title: '',
      key: 'pick',
      width: 44,
      render: (_, row) => (
        <Checkbox
          aria-label={`Nhập file ${row.file.name}`}
          checked={row.selected}
          disabled={
            !!running || !(row.status === 'ready' || row.status === 'failed') || !rowDay(row)
          }
          onChange={(event) => setRows((prev) => toggleRow(prev, row.key, event.target.checked))}
        />
      ),
    },
    {
      title: 'Ngày trong file',
      key: 'day',
      width: 150,
      render: (_, row) => {
        const day = rowDay(row);
        if (!day) return <span className={styles.small}>—</span>;
        const info = groups.get(row.key);
        return (
          <div>
            <strong>{formatDay(day)}</strong> <span className={styles.small}>{weekdayOf(day)}</span>
            {info && info.count > 1 && (
              <div>
                <Tag color={info.identical ? 'default' : 'orange'}>
                  {info.identical ? 'Bản lặp' : `Trùng ngày (${info.count} file)`}
                </Tag>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'File',
      key: 'file',
      render: (_, row) => <span className={styles.wrapAnywhere}>{row.file.name}</span>,
    },
    {
      title: 'Creator · KC · giờ LIVE',
      key: 'nums',
      width: 210,
      render: (_, row) =>
        row.result && row.result.summary.rows ? (
          <span className={styles.small}>
            {formatNumber(row.result.summary.creators)} Creator ·{' '}
            {formatNumber(row.result.totals.diamonds)} KC ·{' '}
            {formatLiveHours(row.result.totals.liveSeconds)}
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 260,
      render: (_, row) => {
        const view = STATUS_VIEW[row.status];
        const r = row.result;
        const star = r?.starEvaluation;
        const lines: string[] = [];
        if (row.error) lines.push(row.error);
        if (row.status === 'blocked' && r) lines.push(...r.blockers.map((b) => b.message));
        if (row.status !== 'done' && isOverwrite(row) && r?.existing) {
          lines.push(
            `Đã nhập lúc ${formatDateTime(r.existing.importedAt)}${
              r.existing.importedByName ? ` bởi ${r.existing.importedByName}` : ''
            } — nhập nữa sẽ ghi đè.`
          );
        }
        if (row.status === 'done' && star) {
          lines.push(
            star.ok
              ? `Chấm Sao: ${formatNumber(star.rewarded)} Creator, +${formatNumber(star.stars)} ⭐`
              : 'Chưa chấm được Sao — chấm lại ở Data Kim cương.'
          );
        }
        const warnings = row.status === 'ready' ? r?.warnings.length || 0 : 0;
        return (
          <div>
            <Space size={4} wrap>
              <Tag
                color={view.color}
                icon={
                  row.status === 'reading' || row.status === 'applying' ? (
                    <LoadingOutlined />
                  ) : row.status === 'done' ? (
                    <CheckCircleFilled />
                  ) : row.status === 'blocked' ||
                    row.status === 'error' ||
                    row.status === 'failed' ? (
                    <CloseCircleFilled />
                  ) : undefined
                }
              >
                {view.label}
              </Tag>
              {row.status === 'ready' && isOverwrite(row) && <Tag color="gold">Ghi đè</Tag>}
              {warnings > 0 && (
                <Tooltip title={r?.warnings.map((w) => w.message).join(' · ')}>
                  <Tag icon={<WarningFilled />} color="warning">
                    {warnings} lưu ý
                  </Tag>
                </Tooltip>
              )}
              {r && (
                <Button size="small" type="link" onClick={() => setDetailKey(row.key)}>
                  Chi tiết
                </Button>
              )}
            </Space>
            {lines.map((line, index) => (
              <div key={index} className={styles.small}>
                {line}
              </div>
            ))}
          </div>
        );
      },
    },
  ];

  const detail = rows.find((row) => row.key === detailKey) || null;

  const applyButton = (
    <Button
      type="primary"
      loading={running === 'apply'}
      disabled={!!running || !selected.length}
      onClick={overwriteCount ? undefined : applyAll}
    >
      Nhập {formatNumber(selected.length)} ngày
    </Button>
  );

  return (
    <div>
      <div className={styles.head}>
        <span className={styles.fileName}>
          {running === 'preview'
            ? `Đang đọc ${readCount + 1}/${rows.length} file…`
            : running === 'apply'
            ? `Đang nhập… (${doneCount} xong)`
            : `${rows.length} file · ${new Set(days).size} ngày`}
        </span>
        <div className={styles.headActions}>
          {running ? (
            <Button
              onClick={() => {
                stopRef.current = true;
              }}
            >
              Dừng sau file này
            </Button>
          ) : (
            <Button onClick={onClose}>{doneCount ? 'Xong' : 'Chọn file khác'}</Button>
          )}
          {overwriteCount > 0 && !running ? (
            <Popconfirm
              title={`Ghi đè ${overwriteCount} ngày đã có số liệu?`}
              description="Các ngày này đã nhập trước đó. Nhập sẽ thay số cũ bằng số trong file."
              okText="Ghi đè và nhập"
              cancelText="Huỷ"
              onConfirm={applyAll}
            >
              {applyButton}
            </Popconfirm>
          ) : (
            applyButton
          )}
        </div>
      </div>

      {!running && (
        <div className={styles.notices}>
          {conflictsOpen.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`Có ${conflictsOpen.length} ngày mà các file khác số nhau: ${daysListText(
                conflictsOpen
              )}`}
              description="Hai file cùng một ngày nhưng số liệu không giống nhau (có thể xuất ở hai thời điểm). Bấm Chi tiết để so, rồi tick đúng một file cho ngày đó; ngày chưa tick sẽ không được nhập."
            />
          )}
          {gaps.length > 0 && (
            <Alert
              type="info"
              showIcon
              message={`Trong khoảng ${formatDay(days[0])} → ${formatDay(
                days[days.length - 1]
              )}, đợt này không có file cho: ${daysListText(gaps)}`}
              description="Ngày đó có thể đã nhập từ trước — xem dải lịch bên dưới."
            />
          )}
          {blockedCount > 0 && (
            <Alert
              type="error"
              showIcon
              message={`${blockedCount} file không nhập được (xem cột Trạng thái).`}
            />
          )}
        </div>
      )}

      <Table<BatchRow>
        rowKey="key"
        size="small"
        columns={columns}
        dataSource={sorted}
        pagination={false}
        scroll={{ x: 820 }}
      />

      <Modal
        open={!!detail?.result}
        width={1000}
        footer={null}
        title={detail ? `${formatDay(rowDay(detail))} · ${detail.file.name}` : ''}
        onCancel={() => setDetailKey(null)}
        destroyOnClose
      >
        {detail?.result && (
          <ImportResultPanel
            result={detail.result as ImportResult}
            kind="DAILY"
            applying={false}
            // Trong hộp chi tiết chỉ xem; nhập bằng nút "Nhập N ngày" của bảng.
            onApply={() => setDetailKey(null)}
            onReset={() => setDetailKey(null)}
            readOnly
          />
        )}
      </Modal>
    </div>
  );
};

export default BatchImport;
